import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { useStore } from "@nanostores/react";
import { $filter } from "./products-store";
import { ArrowRight } from "lucide-react";
import axios from "axios";

declare var __initialState: any;

interface IProductGridProps {
  homepageMode?: boolean;
}

const ProductGrid: React.FunctionComponent<IProductGridProps> = props => {
  const { homepageMode = false } = props;
  const [products, setProducts] = useState([]);
  const filters = useStore($filter);
  const [loading, setLoading] = useState(false);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 51;
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState<{ [key: string]: boolean }>(
    {}
  );
  const addToCartTimeouts = useRef<{
    [key: string]: ReturnType<typeof setTimeout>;
  }>({});
  const [toast, setToast] = useState<{
    visible: boolean;
    productName: string;
  }>({ visible: false, productName: "" });
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper function to safely get products array
  const getProductsArray = (): any[] => {
    try {
      return Array.isArray(products) ? products : [];
    } catch (error) {
      console.warn("Error accessing products:", error);
      return [];
    }
  };

  // Get page number from URL
  const getPageFromURL = (): number => {
    if (typeof window === "undefined") return 1;
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get("page") || "1", 10);
    return page > 0 ? page : 1;
  };

  // Get filters from URL
  const getFiltersFromURL = (): string[] => {
    if (typeof window === "undefined") return [];
    const params = new URLSearchParams(window.location.search);
    const filters: string[] = [];

    params.getAll("filter").forEach(param => {
      const [type, option] = param.split(":");
      if (type && option) {
        filters.push(`${type}:${option}`);
      }
    });

    return filters;
  };

  // Update URL with page number
  const updateURLWithPage = (page: number) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (page === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page.toString());
    }
    window.history.pushState({}, "", url.toString());
  };

  const loadProducts = async (
    filtersToUse: string[] = [],
    pageNum: number = 1
  ) => {
    setLoading(true);

    /**
     * NOTE!!!! IGNORE THE FOLLOWING COMMENT FOR NOW.
     * WE WILL ONLY PULL TAGS FROM THE URL
     * We should load products based on the following criteria:
     *
     * const page = { category: { _id: 'xxxxx' }, productTags: ['tag1', 'tag2'], type: 'category' }
     *
     * - If a category is set in the initial state, load products for that category.
     * - If productTags are set in the initial state, load products matching those tags.
     * - If filters are applied via the filter store, load products matching those filters.
     * - producTags do take precedence
     * - If no filters and not on a category page, load all products.
     */

    try {
      const page = __initialState.page;
      // console.log("Page initial state:", page);
      // console.log("Using filters from store:", filtersToUse);

      const queryParams = new URLSearchParams();
      const allTags: string[] = [];

      // Collect productTags from page (these take precedence and are always included)
      // if (
      //   page.productTags &&
      //   Array.isArray(page.productTags) &&
      //   page.productTags.length > 0
      // ) {
      //   // console.log("Adding productTags from page:", page.productTags);
      //   allTags.push(...page.productTags);
      // }

      // Add filters from the filter store
      if (filtersToUse.length > 0) {
        // console.log("Adding filters from filter store:", filtersToUse);
        allTags.push(...filtersToUse);
      }

      // Add all collected tags to query params
      if (allTags.length > 0) {
        allTags.forEach(tag => {
          queryParams.append("$tags[]", tag);
        });
        queryParams.append("$filterOperation", "AND");
      }

      // // Add category if we're on a category page
      // if (page.category && page.category._id) {
      //   // console.log("Adding category filter:", page.category._id);
      //   queryParams.append("category", page.category._id);
      // }

      // Add pagination params
      queryParams.append("$limit", itemsPerPage.toString());
      queryParams.append("$skip", ((pageNum - 1) * itemsPerPage).toString());
      queryParams.append("$sort[priority]", "1");
      queryParams.append("$sort[_id]", "1");

      const queryString = queryParams.toString();
      const url = `/sfapi/products-ms${queryString ? `?${queryString}` : ""}`;
      // console.log("Fetching products from:", url);

      const response = await fetch(url);
      const data = await response.json();

      const fetchedProducts = data.data || [];
      const total = data.total || 0;

      setProducts(fetchedProducts);
      setTotalCount(total);

      return fetchedProducts;
    } catch (error) {
      console.error("Error fetching products:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Load products on mount and when filters change
  useEffect(() => {
    const pageFromURL = getPageFromURL();

    // console.log("=== PRODUCT GRID EFFECT TRIGGERED ===");
    // console.log("Page from URL:", pageFromURL);
    // console.log("Filters:", filters);

    if (!filters || filters.length === 0) {
      // console.log("No filters, loading all products");
    }

    setCurrentPage(pageFromURL);
    loadProducts(filters, pageFromURL);
  }, [filters]);

  const handleQuantityChange = (productKey: string, value: string) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    setQuantities(q => ({ ...q, [productKey]: qty }));
  };

  const showToast = (productName: string) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ visible: true, productName });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ visible: false, productName: "" });
    }, 4000);
  };

  const dismissToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ visible: false, productName: "" });
  };

  const handleAddToCart = async (product: any) => {
    const payload = {
      product: product.product._id,
      value: quantities[product.key] || 1,
      customerReferenceName: ""
    };
    try {
      setAddingToCart(product.key);
      const res = await axios.post("/sfapi/cart/add-item", payload, {
        headers: { "Content-Type": "application/json" }
      });

      // Update cart counter in the DOM
      const cartCount =
        (res.data && res.data.items && res.data.items.length) || 0;
      document.querySelectorAll(".cartCounter").forEach(e => {
        e.innerHTML = String(cartCount);
      });

      // Show toast notification
      showToast(product.name);

      setAddedToCart(prev => ({ ...prev, [product.key]: true }));
      if (addToCartTimeouts.current[product.key]) {
        clearTimeout(addToCartTimeouts.current[product.key]);
      }
      addToCartTimeouts.current[product.key] = setTimeout(() => {
        setAddedToCart(prev => ({ ...prev, [product.key]: false }));
      }, 2500);
    } catch (error) {
      console.error("Add to cart error", error);
    } finally {
      setAddingToCart(null);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURLWithPage(page);
    loadProducts(filters, page);
    // Scroll to top of products
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentProducts = getProductsArray();
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the beginning
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pt-4 sm:px-6 sm:pt-8 lg:max-w-7xl lg:px-8 lg:pt-12">
      <h2 className="sr-only">Products</h2>

      {/* Toast Notification */}
      {toast.visible && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Added to cart</p>
              <p className="text-sm text-gray-500 truncate">
                {toast.productName}
              </p>
            </div>
            <button
              type="button"
              onClick={dismissToast}
              className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Show products only when not on homepage or when filters are applied */}
      {(!homepageMode || filters.length > 0) && (
        <>
          {loading && (
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="font-medium">Loading products...</span>
            </div>
          )}

          {!loading && products.length === 0 && filters.length > 0 && (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No products found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your filters to see more results.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
            {products.map(product => {
              const isStock = product.product.inventory;
              const isComposite = product.product.isComposite;
              const availableQty =
                product.product.inventoryCount != null
                  ? product.product.inventoryCount
                  : 0;
              const uom = product.product.unitOfMeasure || "Each";
              const price = product.price != null ? product.price : null;
              const qtyValue =
                quantities[product.key] != null ? quantities[product.key] : 1;
              const allowBackorders =
                __initialState.tenant && __initialState.tenant.allowBackorders;
              const outOfStock =
                isStock &&
                availableQty === 0 &&
                !isComposite &&
                !allowBackorders;

              return (
                <div
                  key={product._id}
                  className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  {/* Image Section */}
                  <div className="aspect-3/4 w-full overflow-hidden bg-gray-200 sm:aspect-auto sm:h-96">
                    <a href={`/product/${product.key}`}>
                      <img
                        src={
                          product.product.images[0]
                            ? `https://storage.googleapis.com/icstorage/${product.product.images[0].url}`
                            : ""
                        }
                        alt={product.name}
                        className="size-full object-cover group-hover:opacity-75"
                      />
                    </a>
                    {/* Out of Stock Badge */}
                    {isStock && outOfStock && (
                      <div className="absolute top-2 right-2 rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                        Out of Stock
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="flex flex-1 flex-col space-y-2 p-4">
                    <h3 className="text-sm font-medium text-gray-900">
                      <a href={`/product/${product.key}`}>{product.name}</a>
                    </h3>
                    <p className="text-xs text-gray-500">SKU: {product.key}</p>

                    {/* Available Quantity & UOM */}
                    {isStock && !isComposite && (
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Available Quantity:</span>{" "}
                        {outOfStock ? (
                          <span className="text-red-600 font-medium">
                            Out of Stock
                          </span>
                        ) : (
                          <>
                            {availableQty}
                            <span className="text-gray-500"> ({uom})</span>
                          </>
                        )}
                      </p>
                    )}

                    <div className="flex flex-1 flex-col justify-end">
                      {/* Price */}
                      {price !== null && (
                        <p className="text-base font-medium text-gray-900 mb-3">
                          ${price.toFixed(2)}
                        </p>
                      )}

                      {/* Composite Product: Show View Product Button */}
                      {isComposite ? (
                        <a
                          href={`/product/${product.key}`}
                          className="w-full h-10 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary flex items-center justify-center gap-2 mt-3 text-center"
                        >
                          View Product
                        </a>
                      ) : outOfStock ? (
                        <button
                          type="button"
                          disabled
                          className="mt-3 w-full rounded-md bg-gray-300 px-3 py-2 text-sm font-semibold text-gray-500 shadow-sm cursor-not-allowed"
                        >
                          Out of Stock
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 mt-3">
                          <label
                            htmlFor={`qty-${product.key}`}
                            className="sr-only"
                          >
                            Quantity
                          </label>
                          <input
                            id={`qty-${product.key}`}
                            type="number"
                            min={1}
                            max={isStock ? availableQty : undefined}
                            value={qtyValue}
                            onChange={e =>
                              handleQuantityChange(product.key, e.target.value)
                            }
                            disabled={addingToCart === product.key}
                            className="w-16 h-10 px-2 text-center text-sm font-semibold text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Quantity"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddToCart(product)}
                            disabled={addingToCart === product.key}
                            className="flex-1 h-10 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {addingToCart === product.key ? (
                              <>
                                <svg
                                  className="animate-spin h-4 w-4"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Adding...
                              </>
                            ) : addedToCart[product.key] ? (
                              <>
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414l2.793 2.793 6.793-6.793a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Added
                              </>
                            ) : (
                              "Add to Cart"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <nav className="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0 mt-12">
              <div className="-mt-px flex w-0 flex-1">
                {currentPage > 1 ? (
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  >
                    <svg
                      className="mr-3 size-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Previous
                  </button>
                ) : (
                  <span className="inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium text-gray-300">
                    <svg
                      className="mr-3 size-5 text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Previous
                  </span>
                )}
              </div>
              <div className="hidden md:-mt-px md:flex">
                {getPageNumbers().map((pageNum, idx) => {
                  if (pageNum === "...") {
                    return (
                      <span
                        key={`ellipsis-${idx}`}
                        className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
                      >
                        ...
                      </span>
                    );
                  }
                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum as number)}
                      className={`inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium ${
                        isActive
                          ? "border-primary-700 text-primary"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      }`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <div className="-mt-px flex w-0 flex-1 justify-end">
                {currentPage < totalPages ? (
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  >
                    Next
                    <svg
                      className="ml-3 size-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                ) : (
                  <span className="inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium text-gray-300">
                    Next
                    <svg
                      className="ml-3 size-5 text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                )}
              </div>
            </nav>
          )}

          {/* Results count */}
          {!loading && totalCount > 0 && (
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}{" "}
              results
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;
